import { dateToApiFormat } from '@/helpers/dateHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import {
  UpdateAvailabilityExceptionDetail,
  useUpdateAvailabilityExceptionsMutation
} from '@/services/gql/graphql.generated';
import { hoursToSeconds } from 'date-fns';
import { useCallback, useState } from 'react';
import { EmployeeSchedule } from '../employee/schedule/schedule.types';

export const useSaveSchedule = (startDate: Date, endDate: Date) => {
  const [, updateExceptions] = useUpdateAvailabilityExceptionsMutation();
  const [fetching, setFetching] = useState(false);
  const { unregisterConfirmation } = useConfirm(state => ({ unregisterConfirmation: state.unregisterForConfirmation }));

  const save = useCallback(
    async (employeeId: string, schedule: EmployeeSchedule) => {
      const keys = Object.keys(schedule);
      const exceptions: UpdateAvailabilityExceptionDetail[] = [];

      keys.forEach(key => {
        const date = new Date(key);
        if (dateToApiFormat(date) < dateToApiFormat(startDate) || dateToApiFormat(date) > dateToApiFormat(endDate)) {
          return;
        }
        const scheduleDay = schedule[key];
        const leaveType = scheduleDay.leaveType;

        scheduleDay.availableTimes.forEach(time => {
          const start = time.start || 0;
          const end = time.end || hoursToSeconds(24);
          const timesMatchPattern = time.start === scheduleDay.patternStart && time.end === scheduleDay.patternEnd;

          if (leaveType || (!leaveType && !timesMatchPattern)) {
            // If start is 0, we want to send undefined to the API to indicate availability from the start of the day. If end is 24 hours, we want to send undefined to indicate availability until the end of the day
            exceptions.push({
              date: dateToApiFormat(date),
              start: start ? start : undefined,
              end: end === hoursToSeconds(24) ? undefined : end,
              leaveTypeId: leaveType
            });
          }
        });
      });

      setFetching(true);
      try {
        const result = await updateExceptions({
          exceptions: {
            employeeExceptions: [{ employeeId, exceptions }],
            start: dateToApiFormat(startDate),
            end: dateToApiFormat(endDate)
          }
        });
        await handleMutationResult(result, {
          successMessage: 'Availability saved successfully.',
          errorMessage: 'Failed to save availability.',
          onSuccess: unregisterConfirmation
        });
        return result;
      } finally {
        setFetching(false);
      }
    },
    [updateExceptions, startDate, endDate, unregisterConfirmation]
  );

  return { save, fetching };
};
